/**
 * A caixa de denúncias do comitê (compliance / Lei 14.457/2022).
 *
 * Conteúdo sigiloso: só chega aqui quem tem `denuncia.read`. Traz o resumo, a configuração do
 * canal público (link que a empresa divulga) e a lista para abrir cada apuração.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ActionButton, DataTable, PageShell, type DataTableColumn } from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import denunciaService from '../../services/denunciaService';
import {
  Denuncia,
  DenunciaEstatisticas,
  ConfigDoCanal,
  SITUACOES_DA_DENUNCIA,
  ROTULO_STATUS,
  ROTULO_CATEGORIA,
  COR_DO_STATUS,
  DenunciaStatus,
} from '../../types/denuncia';

const colunas: DataTableColumn<Denuncia>[] = [
  { id: 'protocolo', label: 'Protocolo', render: (d) => <span style={{ fontFamily: 'monospace' }}>{d.protocolo}</span> },
  { id: 'titulo', label: 'Assunto', primary: true, render: (d) => d.titulo },
  { id: 'categoria', label: 'Categoria', render: (d) => ROTULO_CATEGORIA[d.categoria] },
  {
    id: 'status',
    label: 'Situação',
    render: (d) => <Chip size="small" color={COR_DO_STATUS[d.status]} label={ROTULO_STATUS[d.status]} />,
  },
  {
    id: 'recebidaEm',
    label: 'Recebida em',
    render: (d) => new Date(d.recebidaEm).toLocaleDateString('pt-BR'),
  },
];

function ResumoCard({ rotulo, valor }: { rotulo: string; valor: number }): React.ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 120, flex: 1 }}>
      <Typography variant="h4">{valor}</Typography>
      <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
    </Paper>
  );
}

export const DenunciaListPage: React.FC = () => {
  const navigate = useNavigate();
  const configurar = useAcao('denuncia.manage', 'Configurar canal');

  const [denuncias, setDenuncias] = useState<Denuncia[]>([]);
  const [stats, setStats] = useState<DenunciaEstatisticas | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<DenunciaStatus | ''>('');

  const [configAberta, setConfigAberta] = useState(false);
  const [config, setConfig] = useState<ConfigDoCanal | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [lista, estat] = await Promise.all([
        denunciaService.listar(filtroStatus ? { status: filtroStatus } : undefined),
        denunciaService.estatisticas(),
      ]);
      setDenuncias(lista);
      setStats(estat);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar as denúncias.');
    } finally {
      setCarregando(false);
    }
  }, [filtroStatus]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const abrirConfig = useCallback(async () => {
    try {
      setConfig(await denunciaService.obterConfig());
      setConfigAberta(true);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível abrir a configuração.');
    }
  }, []);

  const alternarCanal = useCallback(async (ativo: boolean) => {
    setConfig(await denunciaService.configurar(ativo));
  }, []);

  const linkPublico = config?.token
    ? `${window.location.origin}/canal-de-denuncia/${config.token}`
    : null;

  const codigoIframe = linkPublico
    ? `<iframe src="${linkPublico}" title="Canal de Denúncia" width="100%" height="760" style="border:0;max-width:760px;width:100%"></iframe>`
    : null;

  const linkAcompanhar = `${window.location.origin}/acompanhar-denuncia`;
  const codigoIframeAcompanhar = `<iframe src="${linkAcompanhar}" title="Acompanhar Denúncia" width="100%" height="620" style="border:0;max-width:680px;width:100%"></iframe>`;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Canal de Denúncia' }]}
      title="Canal de Denúncia"
      subtitle="Denúncias recebidas pelo canal da empresa — conteúdo sigiloso, restrito ao comitê"
      actions={
        <ActionButton
          acao="denuncia.manage"
          rotulo="Configurar canal"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => void abrirConfig()}
          disabled={configurar.disabled}
        >
          Configurar canal
        </ActionButton>
      }
    >
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

      {stats && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
          <ResumoCard rotulo="Total" valor={stats.total} />
          <ResumoCard rotulo="Em aberto" valor={stats.abertas} />
          <ResumoCard rotulo="Procedentes" valor={stats.porStatus['PROCEDENTE'] ?? 0} />
        </Stack>
      )}

      <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
        <InputLabel id="fstatus">Situação</InputLabel>
        <Select
          labelId="fstatus"
          label="Situação"
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value as DenunciaStatus | '')}
        >
          <MenuItem value="">Todas</MenuItem>
          {SITUACOES_DA_DENUNCIA.map((s) => (
            <MenuItem key={s} value={s}>{ROTULO_STATUS[s]}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <DataTable
        columns={colunas}
        rows={denuncias}
        getRowId={(d) => d.id}
        loading={carregando}
        onRowClick={(d) => navigate(`/denuncias/${d.id}`)}
        emptyState={{
          title: 'Nenhuma denúncia por aqui',
          description:
            'Quando alguém enviar uma denúncia pelo link público do canal, ela aparece aqui para o comitê apurar. Ative o canal em "Configurar canal" e divulgue o link.',
        }}
      />

      <Dialog open={configAberta} onClose={() => setConfigAberta(false)} fullWidth maxWidth="sm">
        <DialogTitle>Canal de Denúncia da empresa</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={config?.ativo ?? false}
                onChange={(e) => void alternarCanal(e.target.checked)}
              />
            }
            label="Canal ativo"
          />
          <Box sx={{ mt: 2 }}>
            {config?.ativo && linkPublico ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Link público para divulgar (cartaz, intranet, contrato):
                </Typography>
                <TextField value={linkPublico} fullWidth size="small"
                  InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 3 }}>
                  Incorporar o formulário no seu site (iframe):
                </Typography>
                <TextField value={codigoIframe ?? ''} fullWidth size="small" multiline minRows={3}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  onFocus={(e) => e.target.select()} />

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 3 }}>
                  Incorporar o acompanhamento (o relator consulta pelo protocolo):
                </Typography>
                <TextField value={codigoIframeAcompanhar} fullWidth size="small" multiline minRows={3}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  onFocus={(e) => e.target.select()} />
              </>
            ) : (
              <Alert severity="info">Ative o canal para gerar o link público.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigAberta(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default DenunciaListPage;
